import { useState } from "react";
import { Phone, WhatsappLogo, EnvelopeSimple, MapPin, ArrowUpRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/motion";
import { BRAND, ADDRESSES, whatsappLink, telLink } from "@/lib/data";

const SERVICES = [
  "Aluminium Windows & Doors",
  "Sliding Windows & Doors",
  "Architectural Glass",
  "Toughened Glass",
  "Shower Enclosures",
  "LED / Custom Mirrors",
  "Glass Partitions",
  "Glass Railings",
  "Glass Storefronts",
  "Office Glass Cabins",
  "Custom Glass & Aluminium",
];

const initial = { name: "", phone: "", type: "Residential", service: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    const lines = [
      `Hi Nagar Glass, I'd like to request a quote.`,
      ``,
      `Name: ${form.name}`,
      `Phone: ${form.phone}`,
      `Type: ${form.type}`,
      form.service ? `Service: ${form.service}` : null,
      form.message ? `Message: ${form.message}` : null,
    ]
      .filter(Boolean)
      .join("\n");
    window.open(whatsappLink(lines), "_blank");
    toast.success("Opening WhatsApp with your enquiry…");
    setForm(initial);
  };

  return (
    <>
      <PageHeader
        overline="Contact · Ahmedabad"
        title={
          <>
            Talk to <span className="italic">Nagar Glass.</span>
          </>
        }
        description="Reach out on WhatsApp or call directly. For a detailed brief, use the enquiry form and we'll respond on WhatsApp."
        testid="contact-header"
      />

      <section data-testid="contact-main" className="mx-auto max-w-[1400px] px-5 py-20 md:px-8 md:py-28">
        <div className="grid grid-cols-1 gap-14 md:grid-cols-12 md:gap-16">
          {/* LEFT: prominent contact */}
          <div className="md:col-span-5">
            <p className="overline text-muted-foreground">Direct lines</p>
            <div className="mt-6 space-y-5">
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp"
                className="group flex items-center justify-between border-t-2 border-ink py-6"
              >
                <div className="flex items-center gap-5">
                  <WhatsappLogo weight="fill" size={26} className="text-gold" />
                  <div>
                    <span className="text-[0.62rem] font-semibold uppercase tracking-[0.2em] text-muted-foreground">WhatsApp</span>
                    <div className="mt-2 font-display text-2xl font-medium tracking-tight text-ink md:text-3xl">
                      {BRAND.phoneDisplay}
                    </div>
                  </div>
                </div>
                <ArrowUpRight size={22} className="text-muted-foreground transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-gold" />
              </a>
              <a
                href={telLink()}
                data-testid="contact-call"
                className="group flex items-center justify-between border-t border-black/15 py-6"
              >
                <div className="flex items-center gap-5">
                  <Phone weight="fill" size={26} className="text-gold" />
                  <div>
                    <span className="text-[0.62rem] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Call</span>
                    <div className="mt-2 font-display text-2xl font-medium tracking-tight text-ink md:text-3xl">
                      {BRAND.phoneDisplay}
                    </div>
                  </div>
                </div>
                <ArrowUpRight size={22} className="text-muted-foreground transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-gold" />
              </a>
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="contact-email"
                className="group flex items-center justify-between border-y border-black/15 py-6"
              >
                <div className="flex items-center gap-5">
                  <EnvelopeSimple size={26} className="text-gold" />
                  <div>
                    <span className="text-[0.62rem] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Email</span>
                    <div className="mt-2 font-display text-lg font-medium tracking-tight text-ink md:text-xl">
                      {BRAND.email}
                    </div>
                  </div>
                </div>
                <ArrowUpRight size={22} className="text-muted-foreground transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-gold" />
              </a>
            </div>

            {/* Addresses */}
            <div className="mt-12 space-y-8">
              {ADDRESSES.map((a, i) => (
                <Reveal key={a.label} delay={i * 0.1}>
                  <div>
                    <div className="flex items-center gap-3">
                      <MapPin weight="fill" size={18} className="text-gold" />
                      <span className="text-[0.62rem] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{a.label}</span>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-ink md:text-base">{a.lines}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* RIGHT: form */}
          <div className="md:col-span-7">
            <h2 className="font-display text-3xl font-medium tracking-tight text-ink md:text-4xl">
              Request <span className="italic">a quote.</span>
            </h2>
            <form onSubmit={onSubmit} data-testid="enquiry-form" className="mt-10 space-y-8">
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                <Field label="Name" required>
                  <input
                    value={form.name}
                    onChange={set("name")}
                    data-testid="input-name"
                    placeholder="Your full name"
                    className="w-full border-0 border-b border-black/20 bg-transparent py-3 font-body text-base text-ink placeholder:text-muted-foreground/60 focus:border-ink focus:outline-none"
                  />
                </Field>
                <Field label="Phone" required>
                  <input
                    value={form.phone}
                    onChange={set("phone")}
                    data-testid="input-phone"
                    placeholder="+91 ..."
                    className="w-full border-0 border-b border-black/20 bg-transparent py-3 font-body text-base text-ink placeholder:text-muted-foreground/60 focus:border-ink focus:outline-none"
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                <Field label="Type">
                  <div className="mt-1 flex gap-2">
                    {["Residential", "Commercial"].map((t) => (
                      <button
                        type="button"
                        key={t}
                        data-testid={`type-${t.toLowerCase()}`}
                        onClick={() => setForm((s) => ({ ...s, type: t }))}
                        className={`rounded-sm border px-4 py-2 text-[0.68rem] font-semibold uppercase tracking-[0.14em] transition-colors ${
                          form.type === t
                            ? "border-ink bg-ink text-offwhite"
                            : "border-black/15 text-muted-foreground hover:border-ink hover:text-ink"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Required Service">
                  <select
                    value={form.service}
                    onChange={set("service")}
                    data-testid="input-service"
                    className="w-full appearance-none border-0 border-b border-black/20 bg-transparent py-3 font-body text-base text-ink focus:border-ink focus:outline-none"
                  >
                    <option value="">Select a service…</option>
                    {SERVICES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="Message">
                <textarea
                  value={form.message}
                  onChange={set("message")}
                  data-testid="input-message"
                  rows={4}
                  placeholder="Tell us a bit about your space and requirement…"
                  className="w-full resize-none border-0 border-b border-black/20 bg-transparent py-3 font-body text-base text-ink placeholder:text-muted-foreground/60 focus:border-ink focus:outline-none"
                />
              </Field>

              <button
                type="submit"
                data-testid="submit-enquiry"
                className="group inline-flex items-center gap-2.5 rounded-sm bg-ink px-8 py-4 text-xs font-semibold uppercase tracking-[0.16em] text-offwhite transition-colors duration-500 hover:bg-gold hover:text-ink"
              >
                Request a Quote
                <ArrowUpRight size={16} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </button>
              <p className="mt-3 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                Submitting opens WhatsApp with your details prefilled.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Maps embeds */}
      <section className="border-t border-black/10 bg-cream/40 py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <span className="eyebrow rule">Visit a Showroom</span>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
            {ADDRESSES.map((a, i) => (
              <Reveal key={a.label} delay={i * 0.1}>
                <div data-testid={`map-${i}`} className="overflow-hidden border border-black/10 bg-white">
                  <div className="relative aspect-[16/10] w-full overflow-hidden">
                    <iframe
                      title={a.label}
                      src={a.mapEmbed}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      className="h-full w-full border-0 grayscale-[15%]"
                      allowFullScreen
                    />
                  </div>
                  <div className="p-6 md:p-7">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <span className="text-[0.62rem] font-semibold uppercase tracking-[0.2em] text-gold">{a.label}</span>
                        <p className="mt-3 text-sm leading-relaxed text-ink">{a.lines}</p>
                      </div>
                      <a
                        href={a.mapLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`directions-${i}`}
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-sm border border-ink px-4 py-2 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-ink transition-colors hover:bg-ink hover:text-offwhite"
                      >
                        Directions <ArrowUpRight size={13} />
                      </a>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-[0.62rem] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
      {label} {required && <span className="text-gold">*</span>}
    </span>
    <div className="mt-3">{children}</div>
  </label>
);

export default Contact;
